import * as THREE from "three";
import type { DishKind } from "../game/dishes";

// The dishes, real size (a dinner plate is 26 cm across), each with its grime
// as blobs that disappear one per scrub, the same as `grime` counts down.

const lathe = (pts: [number, number][], segments = 28) => new THREE.LatheGeometry(pts.map(([r, y]) => new THREE.Vector2(r, y)), segments);

const GEOMETRY = {
  plate: lathe([
    [0, 0.004],
    [0.085, 0.004],
    [0.09, 0],
    [0.1, 0.004],
    [0.128, 0.02],
    [0.13, 0.024],
    [0.126, 0.024],
    [0.098, 0.012],
    [0, 0.012],
  ]),
  bowl: lathe([
    [0, 0],
    [0.035, 0],
    [0.04, 0.008],
    [0.07, 0.025],
    [0.088, 0.06],
    [0.09, 0.064],
    [0.084, 0.064],
    [0.064, 0.03],
    [0.03, 0.016],
    [0, 0.016],
  ]),
  mug: lathe([
    [0, 0],
    [0.04, 0],
    [0.044, 0.006],
    [0.045, 0.1],
    [0.04, 0.1],
    [0.039, 0.012],
    [0, 0.012],
  ]),
  // A handleless tea cup, 8 cm across.
  cup: lathe([
    [0, 0],
    [0.025, 0],
    [0.027, 0.006],
    [0.04, 0.05],
    [0.042, 0.062],
    [0.038, 0.062],
    [0.035, 0.05],
    [0.022, 0.01],
    [0, 0.01],
  ]),
  // A little round sauce dish, 9 cm across.
  sauce: lathe([
    [0, 0],
    [0.028, 0],
    [0.04, 0.012],
    [0.045, 0.022],
    [0.041, 0.022],
    [0.034, 0.012],
    [0, 0.008],
  ]),
  // A carbon-steel wok, 34 cm across, with a long wooden handle (`wokHandle`).
  wok: lathe(
    [
      [0, 0],
      [0.06, 0.004],
      [0.11, 0.022],
      [0.15, 0.06],
      [0.17, 0.1],
      [0.172, 0.104],
      [0.166, 0.104],
      [0.144, 0.062],
      [0.104, 0.026],
      [0.056, 0.01],
      [0, 0.006],
    ],
    36,
  ),
  wokHandle: new THREE.CylinderGeometry(0.016, 0.019, 0.24, 10),
  // An espresso shot glass, 7 cm tall.
  shotglass: lathe([
    [0, 0],
    [0.022, 0],
    [0.026, 0.07],
    [0.023, 0.07],
    [0.019, 0.008],
    [0, 0.008],
  ]),
  // A steel milk pitcher, 11 cm tall, narrowing to the rim.
  pitcher: lathe([
    [0, 0],
    [0.042, 0],
    [0.045, 0.06],
    [0.036, 0.11],
    [0.033, 0.11],
    [0.041, 0.06],
    [0.038, 0.004],
    [0, 0.004],
  ]),
  // A plastic soda cup, 14 cm tall: no lid, no straw.
  tumbler: lathe([
    [0, 0],
    [0.03, 0],
    [0.042, 0.14],
    [0.039, 0.14],
    [0.028, 0.004],
    [0, 0.004],
  ]),
  // A red plastic burger basket, 18 cm square: a lathe with four sides.
  basket: lathe(
    [
      [0, 0],
      [0.095, 0],
      [0.125, 0.05],
      [0.13, 0.055],
      [0.123, 0.055],
      [0.092, 0.008],
      [0, 0.008],
    ],
    4,
  ),
  handle: new THREE.TorusGeometry(0.028, 0.008, 8, 16, Math.PI),
  blob: new THREE.SphereGeometry(1, 12, 8),
};

const BODY_COLOR: Record<DishKind, number> = { plate: 0xfbfbf6, mug: 0x8fcfc4, bowl: 0xf6dfa4, cup: 0xe9efe6, sauce: 0x3b6fa8, wok: 0x2f3235, basket: 0xc8261f, tumbler: 0xe9eef2, shotglass: 0xdfe9ee, pitcher: 0xc9d0d6 };
const GRIME = {
  yolk: new THREE.MeshStandardMaterial({ color: 0xf2a91e, roughness: 0.5 }),
  syrup: new THREE.MeshStandardMaterial({ color: 0x7a3f17, roughness: 0.25 }),
  coffee: new THREE.MeshStandardMaterial({ color: 0x4a2c17, roughness: 0.4 }),
  oat: new THREE.MeshStandardMaterial({ color: 0xc8ad80, roughness: 0.9 }),
  tea: new THREE.MeshStandardMaterial({ color: 0x9c6a2a, roughness: 0.3 }),
  soy: new THREE.MeshStandardMaterial({ color: 0x2a1308, roughness: 0.15 }),
  chili: new THREE.MeshStandardMaterial({ color: 0xc23b16, roughness: 0.2 }),
  grease: new THREE.MeshStandardMaterial({ color: 0x5a3a12, roughness: 0.35 }),
  burnt: new THREE.MeshStandardMaterial({ color: 0x1a1009, roughness: 0.9 }),
};

/** [x, y, z, sx, sy, sz, material] for each blob, in the order they come off. */
const BLOBS: Record<DishKind, [number, number, number, number, number, number, keyof typeof GRIME][]> = {
  plate: [
    [-0.03, 0.013, 0.01, 0.035, 0.006, 0.03, "yolk"],
    [0.035, 0.013, -0.02, 0.04, 0.004, 0.022, "syrup"],
  ],
  mug: [[0, 0.075, 0, 0.041, 0.006, 0.041, "coffee"]],
  bowl: [
    [-0.02, 0.022, 0.01, 0.03, 0.012, 0.025, "oat"],
    [0.025, 0.026, -0.015, 0.028, 0.012, 0.03, "oat"],
    [0.0, 0.03, 0.035, 0.022, 0.01, 0.02, "oat"],
  ],
  basket: [
    [-0.035, 0.012, 0.025, 0.035, 0.004, 0.025, "chili"],
    [0.03, 0.012, -0.025, 0.03, 0.004, 0.02, "grease"],
  ],
  cup: [[0, 0.012, 0, 0.028, 0.004, 0.028, "tea"]],
  tumbler: [[0, 0.01, 0, 0.026, 0.006, 0.026, "syrup"]],
  shotglass: [[0, 0.012, 0, 0.018, 0.004, 0.018, "coffee"]],
  pitcher: [
    [0, 0.01, 0, 0.034, 0.004, 0.034, "oat"],
    [0.02, 0.05, 0.02, 0.012, 0.02, 0.01, "oat"],
  ],
  sauce: [[0.005, 0.01, 0, 0.026, 0.004, 0.022, "soy"]],
  wok: [
    [-0.03, 0.014, 0.02, 0.06, 0.008, 0.05, "grease"],
    [0.05, 0.022, -0.03, 0.045, 0.01, 0.04, "burnt"],
    [-0.06, 0.03, -0.05, 0.04, 0.008, 0.035, "chili"],
    [0.02, 0.012, 0.06, 0.05, 0.007, 0.04, "grease"],
  ],
};

/** The red-and-white checked paper lining a burger basket. */
const PAPER = new THREE.MeshStandardMaterial({ roughness: 0.9, map: checks() });

function checks(): THREE.CanvasTexture | null {
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d");
  if (!g) return null;
  for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) {
    g.fillStyle = (x + y) % 2 ? "#f4efe6" : "#d3352b";
    g.fillRect(x * 8, y * 8, 8, 8);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export interface DishModel {
  group: THREE.Group;
  /** The dish's own material, for the aim highlight. */
  body: THREE.MeshStandardMaterial;
  blobs: THREE.Mesh[];
  /** Shows `grime` of the blobs. */
  setGrime(grime: number): void;
}

export function makeDish(kind: DishKind): DishModel {
  const group = new THREE.Group();
  const metal = kind === "wok" || kind === "pitcher";
  const body = new THREE.MeshStandardMaterial({
    color: BODY_COLOR[kind],
    roughness: metal ? 0.45 : 0.25,
    metalness: metal ? 0.6 : 0,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(GEOMETRY[kind], body);
  mesh.castShadow = true;
  // A soda cup is clear plastic, frosted: you can see how full it is from the side.
  if (kind === "tumbler") {
    body.transparent = true;
    body.opacity = 0.25;
    body.depthWrite = false;
    body.roughness = 0.6;
    mesh.renderOrder = 2;
    mesh.castShadow = false;
  }
  // The basket's four-sided lathe, turned square to the world.
  if (kind === "basket") {
    mesh.rotation.y = Math.PI / 4;
    const paper = new THREE.Mesh(new THREE.PlaneGeometry(0.15, 0.15), PAPER);
    paper.rotation.x = -Math.PI / 2;
    paper.position.y = 0.0095;
    group.add(paper);
  }
  group.add(mesh);
  if (kind === "mug") {
    const handle = new THREE.Mesh(GEOMETRY.handle, body);
    handle.rotation.z = -Math.PI / 2;
    handle.position.set(0.045, 0.05, 0);
    group.add(handle);
  }
  if (kind === "wok") {
    const handle = new THREE.Mesh(GEOMETRY.wokHandle, new THREE.MeshStandardMaterial({ color: 0x8a5a2b, roughness: 0.7 }));
    handle.rotation.z = Math.PI / 2 - 0.25;
    handle.position.set(0.28, 0.13, 0);
    group.add(handle);
  }
  const blobs = BLOBS[kind].map(([x, y, z, sx, sy, sz, mat]) => {
    const blob = new THREE.Mesh(GEOMETRY.blob, GRIME[mat]);
    blob.position.set(x, y, z);
    blob.scale.set(sx, sy, sz);
    group.add(blob);
    return blob;
  });
  return {
    group,
    body,
    blobs,
    setGrime(grime) {
      blobs.forEach((b, i) => (b.visible = i < grime));
    },
  };
}

/** How a dish sits: plates flat in the tub, on edge in a rack; cups and bowls upside down in a rack. */
export function poseDish(group: THREE.Object3D, kind: DishKind, where: "upright" | "racked", jitter = 0): void {
  group.rotation.set(0, jitter * 2.4, 0);
  if (where === "racked") {
    // On edge, face to the side, so a row of plates (or baskets) stands across the rack.
    if (kind === "plate" || kind === "basket") group.rotation.set(0, 0, Math.PI / 2);
    else group.rotation.set(Math.PI, jitter, 0);
  } else if (kind === "plate") {
    group.rotation.x = (jitter - 0.5) * 0.35;
  }
}

/** A 50 cm dish rack: a grey plastic tray with a grid of pegs. */
export function makeRack(): THREE.Group {
  const rack = new THREE.Group();
  const plastic = new THREE.MeshStandardMaterial({ color: 0x55606b, roughness: 0.6 });
  const side = new THREE.BoxGeometry(0.5, 0.09, 0.012);
  const end = new THREE.BoxGeometry(0.012, 0.09, 0.5);
  for (const z of [-0.244, 0.244]) {
    const m = new THREE.Mesh(side, plastic);
    m.position.set(0, 0.045, z);
    rack.add(m);
  }
  for (const x of [-0.244, 0.244]) {
    const m = new THREE.Mesh(end, plastic);
    m.position.set(x, 0.045, 0);
    rack.add(m);
  }
  const bar = new THREE.BoxGeometry(0.5, 0.01, 0.012);
  for (let i = 0; i < 6; i++) {
    const m = new THREE.Mesh(bar, plastic);
    m.position.set(0, 0.005, -0.21 + i * 0.084);
    rack.add(m);
  }
  rack.traverse((o) => (o.castShadow = true));
  return rack;
}

/** Where slot `i` of an 8-slot rack is, relative to the rack. */
export function rackSlot(i: number): [number, number, number] {
  const col = i % 4;
  const row = Math.floor(i / 4);
  return [-0.18 + col * 0.12, 0.012, -0.12 + row * 0.24];
}

/** How far above a rack's floor a racked dish's origin sits. */
export function rackLift(kind: DishKind): number {
  return { plate: 0.13, mug: 0.1, bowl: 0.064, cup: 0.062, sauce: 0.022, wok: 0.104, basket: 0.09, tumbler: 0.14, shotglass: 0.07, pitcher: 0.11 }[kind];
}
